import { useController, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal, Button, Input, Select } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { useCreateBonus } from '@/features/bonuses/hooks/useBonuses'
import { useBonusList } from '@/features/bonuses/hooks/useBonuses'
import { useCompanyMembers } from '@/features/companies/hooks/useCompanies'
import {
  createBonusSchema,
  getDefaultPayrollYearMonth,
  BONUS_PAYROLL_MONTH_ERROR_MESSAGE,
  type CreateBonusFormData,
} from '@/features/bonuses/schemas/bonus.schema'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { bonusTypeLabel } from '@/features/bonuses/components/bonusUtils'
import { getMemberId, getMemberLabel, getMemberSubline } from '@/features/companies/utils/memberLabel'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'
import type { BonusType } from '@/features/bonuses/types/bonus.types'

const BONUS_TYPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Select type…' },
  { value: 'performance', label: 'Performance' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'taxable', label: 'Taxable' },
  { value: 'non_taxable', label: 'Non-taxable' },
]

interface CreateBonusModalProps {
  companyId: string
  open: boolean
  onClose: () => void
}

export function CreateBonusModal({
  companyId,
  open,
  onClose,
}: CreateBonusModalProps): React.ReactElement | null {
  const createMutation = useCreateBonus()
  const { data: membersPage } = useCompanyMembers(open ? companyId : null, { page: 1, limit: 100 })
  const members = membersPage?.data ?? []
  const { data: existingBonusesPage } = useBonusList(
    open ? companyId : null,
    { page: 1, limit: 100 },
  )
  const existingBonuses = existingBonusesPage?.data ?? []
  const { currency: orgCurrency, currencyOptions } = useOrgCurrency()

  const defaults = getDefaultPayrollYearMonth()

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setError,
    formState: { errors },
  } = useForm<CreateBonusFormData>({
    resolver: zodResolver(createBonusSchema),
    defaultValues: {
      userId: '',
      year: defaults.year,
      month: defaults.month,
      bonusType: undefined,
      amount: undefined,
      currency: orgCurrency,
      reason: '',
    },
  })

  const { field: userIdField } = useController({ name: 'userId', control })
  const { field: bonusTypeField } = useController({ name: 'bonusType', control })
  const { field: currencyField } = useController({ name: 'currency', control })

  const watchedUserId = watch('userId')
  const watchedBonusType = watch('bonusType')
  const watchedYear = watch('year')
  const watchedMonth = watch('month')

  const memberOptions: SelectOption[] = members.map((m) => ({
    value: getMemberId(m),
    label: getMemberLabel(m),
    description: getMemberSubline(m),
  }))

  const isDuplicate =
    !!watchedUserId &&
    !!watchedBonusType &&
    existingBonuses.some(
      (b) =>
        b.userId === watchedUserId &&
        b.bonusType === watchedBonusType &&
        b.year === watchedYear &&
        b.month === watchedMonth &&
        b.status !== 'reversed',
    )

  const onSubmit = (data: CreateBonusFormData) => {
    createMutation.mutate(
      {
        companyId,
        body: {
          userId: data.userId,
          year: data.year,
          month: data.month,
          bonusType: data.bonusType as BonusType,
          amount: data.amount,
          currency: data.currency,
          reason: data.reason || undefined,
        },
      },
      {
        onSuccess: () => {
          reset()
          onClose()
        },
        onError: (err) => {
          const msg = getErrorMessage(err)
          if (
            msg === BONUS_PAYROLL_MONTH_ERROR_MESSAGE ||
            msg.toLowerCase().includes('current payroll month')
          ) {
            setError('year', { type: 'server', message: msg })
            setError('month', { type: 'server', message: msg })
          }
        },
      },
    )
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Issue bonus"
      footer={
        <div className="flex gap-2">
          <Button
            type="submit"
            form="create-bonus-form"
            variant="primary"
            loading={createMutation.isPending}
          >
            {createMutation.isPending ? 'Issuing…' : 'Issue bonus'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
        </div>
      }
    >
      <form id="create-bonus-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="bonus-userId"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Employee <span className="text-red-500">*</span>
          </label>
          <Select
            id="bonus-userId"
            options={memberOptions}
            value={userIdField.value ?? ''}
            onChange={userIdField.onChange}
            placeholder="Select employee…"
            searchable
          />
          {errors.userId && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.userId.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="bonus-bonusType"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Bonus type <span className="text-red-500">*</span>
          </label>
          <Select
            id="bonus-bonusType"
            options={BONUS_TYPE_OPTIONS}
            value={bonusTypeField.value ?? ''}
            onChange={bonusTypeField.onChange}
          />
          {errors.bonusType && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.bonusType.message}
            </p>
          )}
          {isDuplicate && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              A {watchedBonusType ? bonusTypeLabel(watchedBonusType as BonusType) : ''} bonus
              already exists for this employee and period. Submitting will return an error.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            {...register('year', { valueAsNumber: true })}
            id="bonus-year"
            label="Year"
            type="number"
            min="2000"
            max="2100"
            required
            error={errors.year?.message}
            hint="Must match the current payroll year"
          />
          <Input
            {...register('month', { valueAsNumber: true })}
            id="bonus-month"
            label="Month"
            type="number"
            min="1"
            max="12"
            required
            error={errors.month?.message}
            hint="Must match the current payroll month"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            {...register('amount', { valueAsNumber: true })}
            id="bonus-amount"
            label="Amount"
            type="number"
            step="0.01"
            min="0.01"
            required
            placeholder="0.00"
            error={errors.amount?.message}
          />
          <div>
            <label
              htmlFor="bonus-currency"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Currency
            </label>
            <Select
              id="bonus-currency"
              options={currencyOptions}
              value={currencyField.value ?? orgCurrency}
              onChange={currencyField.onChange}
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="bonus-reason"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Reason
          </label>
          <textarea
            {...register('reason')}
            id="bonus-reason"
            rows={3}
            placeholder="Describe the reason for this bonus…"
            className="box-border w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
          {errors.reason && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.reason.message}</p>
          )}
        </div>
      </form>
    </Modal>
  )
}

CreateBonusModal.displayName = 'CreateBonusModal'
